import pick from 'lodash/pick';
import flatten from 'lodash/flatten';
import flow from 'lodash/fp/flow';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import { DateTime } from 'luxon';
import { getStore } from '~/mst/utils';
import { PERMISSIONS } from '~/mst/models/permissions/types';
import { RolesEnum } from '~/utils/constants';
import type { IUserModel } from './model';

export default (self: IUserModel) => {
  function fullName() {
    return self.first_name && self.last_name ? `${self.first_name} ${self.last_name}` : '';
  }
  return {
    get fullName() {
      return fullName();
    },
    // "JS" for John Smith; the one name's or the email's first letter when a name is missing
    get initials() {
      const letters = [self.first_name, self.last_name].filter(Boolean).map((name) => name!.trim().charAt(0));
      const initials = letters.length ? letters.join('') : self.email?.charAt(0);
      return initials ? initials.toUpperCase() : null;
    },
    get presentName() {
      return fullName();
    },
    // the roles and organizations of the organizations this account can see
    get visibleOrganizationsRoles() {
      const { organizations } = getStore(self);
      return self.organizationsRoles.filter(({ organizationId, role }) => role && organizations.getById(organizationId));
    },
    get userRoleFilterTags() {
      return uniq(self.visibleOrganizationsRoles.map(({ role }) => role));
    },
    get userOrganizationFilterTags() {
      const { organizations } = getStore(self);
      return uniq(self.visibleOrganizationsRoles.map(({ organizationId }) => organizations.getById(organizationId)?.name).filter(Boolean));
    },
    // "last 7 days" users are also "last 30 days" users, so either option finds them
    get lastLoginFilterTags() {
      if (!self.last_login_at) return ['never'];
      const days = DateTime.now().diff(DateTime.fromMillis(self.last_login_at), 'days').days;
      if (days <= 7) return ['last-7-days', 'last-30-days'];
      if (days <= 30) return ['last-30-days'];
      if (days > 90) return ['over-90-days'];
      return [];
    },
    get filterTags() {
      return [...self.userRoleFilterTags, ...self.userOrganizationFilterTags, ...self.lastLoginFilterTags].map((tag) => tag.toLowerCase());
    },
    get searchString() {
      const searchFields = ['presentName', 'email'];
      return Object.values(pick(self, searchFields)).join(' ').toLowerCase();
    },
    hasPermission(key: PERMISSIONS, id?: string) {
      if (id) {
        return Boolean(self.permissions[key]) && self.permissions[key].includes(id);
      }
      return Boolean(self.permissions[key]);
    },
    isManager(organizationId?: string) {
      return self.hasPermission(PERMISSIONS.ManageDevices, organizationId);
    },
    isOwner(organizationId?: string) {
      return self.hasPermission(PERMISSIONS.ManageOrganizations, organizationId);
    },
    get isSupport() {
      return self.hasPermission(PERMISSIONS.AccessContext);
    },
    // support, or granted admin_devices in any organization (as a support session does)
    get isAdmin() {
      return self.isSupport || self.hasPermission(PERMISSIONS.AdminDevices);
    },
    get organizationsIds() {
      return flow(flatten, uniq, compact)(Object.values(self.permissions.toJSON()));
    },
    get isAethair() {
      return self.organizationsIds.find((id) => getStore(self).organizations.getById(id)?.isAethair);
    },
    getRoleByOrganizationId(id: string) {
      if (self.hasPermission(PERMISSIONS.AdminDevices, id)) {
        return RolesEnum.Admin;
      }
      if (self.hasPermission(PERMISSIONS.ManageOrganizations, id)) {
        return RolesEnum.Owner;
      }
      if (self.hasPermission(PERMISSIONS.ManageUsers, id)) {
        return RolesEnum.Manager;
      }
      if (self.hasPermission(PERMISSIONS.ReadUsers, id)) {
        return RolesEnum.User;
      }
      if (self.hasPermission(PERMISSIONS.ReadOrganizations, id)) {
        return RolesEnum.Partner;
      }
      return null;
    },
    get organizationsRoles() {
      return self.organizationsIds.map((id) => {
        const role = self.getRoleByOrganizationId(id);
        return {
          id: `${id}_${role}`,
          organizationId: id,
          role
        };
      });
    }
  };
};
